import { useState, useEffect, useCallback } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutDashboard } from "lucide-react";
import PortalShellPageHeader from "../../components/Layout/PortalShellPageHeader/PortalShellPageHeader";
import styles from "../../layout/portalPageShell.module.css";
import ProductsServicesPanel from "../../components/Layout/ProductsServicesPanel/ProductsServicesPanel";
import ServiceMonitoring from "../../components/Layout/ServiceMonitoring/ServiceMonitoring";
import PortalUserStrip from "../../components/Layout/PortalUserStrip/PortalUserStrip";
import { useAuth } from "../../context/authContext";
import { getUserSubscriptionPlans } from "../../services/subscription-plan-service";
import { getUserDomainPlans } from "../../services/domain-plan-service";

export default function PortalPage() {
  const { user } = useAuth();
  const location = useLocation();
  const { t } = useTranslation(["common", "dashboard"]);

  const [plans, setPlans] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      if (!user) {
        setPlans([]);
        setDomains([]);
        return;
      }

      if (user.role === "admin" || user.role === "owner") {
        setPlans([]);
        setDomains([]);
        return;
      }

      const [plansData, domainsData] = await Promise.all([
        getUserSubscriptionPlans(),
        getUserDomainPlans(),
      ]);

      const planList = plansData.success ? plansData.data || [] : [];
      const domainList = domainsData.success ? domainsData.data || [] : [];

      setPlans(planList);
      setDomains(domainList);
    } catch {
      setPlans([]);
      setDomains([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData, location.key]);

  useEffect(() => {
    if (location.pathname !== "/") return;

    const onVisibility = () => {
      if (document.visibilityState === "visible" && user) {
        fetchData();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [fetchData, location.pathname, user]);

  const isAdminOrOwner =
    user?.role === "admin" || user?.role === "owner";
  if (user && isAdminOrOwner) {
    return <Navigate to="/admin" replace />;
  }

  const numberOfWebsites = plans.filter(
    (plan) => plan.projectType === "Web Application",
  ).length;
  const numberOfMobileApps = plans.filter(
    (plan) => plan.projectType === "Mobile Application",
  ).length;

  const productsData = {
    numberOfWebsites,
    numberOfDomains: domains.length,
    numberOfMobileApps,
    numberOfSubscriptions: plans.filter((plan) => plan.status === "active")
      .length,
    subscriptionPlan: t("dashboard:productsPanel.subscriptionBadge"),
  };

  if (loading) {
    return (
      <div className={styles.portalPage}>
        <div className={styles.container}>
          <PortalShellPageHeader
            icon={
              <LayoutDashboard
                size={28}
                strokeWidth={1.75}
                absoluteStrokeWidth
              />
            }
            title={t("dashboard:title")}
            subtitle={t("dashboard:pageSubtitle")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.portalPage}>
      <div className={styles.container}>
        <PortalShellPageHeader
          icon={
            <LayoutDashboard
              size={28}
              strokeWidth={1.75}
              absoluteStrokeWidth
            />
          }
          title={t("dashboard:title")}
          subtitle={t("dashboard:pageSubtitle")}
        />

        {user ? (
          <section className={styles.userStripSection} aria-label={t("dashboard:portalUser.sectionLabel")}>
            <PortalUserStrip user={user} />
          </section>
        ) : null}

        <section className={styles.overviewSection} aria-labelledby="portal-overview-heading">
          <div className={styles.overviewScale}>
            <h2 id="portal-overview-heading" className={styles.sectionLabel}>
              {t("dashboard:sectionOverview")}
            </h2>
            <div className={styles.overviewProducts}>
              <ProductsServicesPanel products={productsData} />
            </div>
          </div>
        </section>

        <section
          className={styles.monitoringSection}
          aria-label={t("dashboard:sectionMonitoring")}
        >
          <ServiceMonitoring plans={plans} domains={domains} />
        </section>
      </div>
    </div>
  );
}
